import {useEffect, useState, useMemo, useCallback} from 'react';
import {kr} from '../lib/format.js';
import {api} from '../lib/auth.js';

/**
 * Scenario view over the article register: price x units per customer x customers, per
 * article, against what each article actually charges today.
 *
 * NOTHING HERE IS BOOKED. Jabr reports on the books and does not keep them, and the
 * repo rule is that preliminary figures never become vouchers — so a scenario lives in
 * the browser, is labelled as a scenario everywhere it appears, and never reaches
 * Fortnox or Mongo. The one number on this page that is not a scenario is `Nuläge`,
 * which is the booked run-rate the scenario is measured against.
 *
 * The seed is the last three FULLY booked months. August is left out for the same
 * reason burn and runway leave it out: it is partly booked, and averaging it in would
 * quietly understate every rate on the page.
 */

const STORE = 'jabr.simulation.v1';
const PER = {seat: 'licens', order: 'order', transaction: 'transaktion', hour: 'timme',
	bundle: 'paket'};

/**
 * A scenario row is only the three inputs; everything else is recomputed from them.
 *
 * Seeded from what is actually INVOICED, never from the register's list price, so the
 * three multiply back to the booked run-rate and a freshly seeded page reads exactly the
 * same total as `Nuläge`. That invariant is the point: every krona of difference on this
 * page is then something the user changed, not something the seed decided for them. The
 * list price sits in its own column to reach for deliberately.
 */
const seedRow = a => ({
	price: a.actual.price ?? a.price_sek ?? 0,
	units: a.actual.units || 0,
	customers: a.actual.customers || 0,
	on: a.actual.run_rate > 0,
});

const num = v => (Number.isFinite(Number(v)) ? Number(v) : 0);

function Field({value, onChange, width = 74, step = 1, title}) {
	return (
		<input className="sim__in" type="number" inputMode="decimal" step={step} min="0"
			style={{width}} value={value} title={title}
			onChange={e => onChange(e.target.value === '' ? '' : Number(e.target.value))} />
	);
}

export default function Simulate() {
	const [data, setData] = useState(null);
	const [error, setError] = useState(null);
	const [rows, setRows] = useState({});

	useEffect(() => {
		api('/api/simulation').then(r => r.json())
			.then(d => {
				if (d.error) return setError(d.error);
				setData(d);
				// A saved scenario is keyed by article, so a new article in the register shows
				// up seeded rather than missing, and a removed one is simply ignored.
				let saved = {};
				try { saved = JSON.parse(localStorage.getItem(STORE) ?? '{}'); } catch { saved = {}; }
				setRows(Object.fromEntries(d.articles.map(a => [a._id, {...seedRow(a), ...saved[a._id]}])));
			})
			.catch(e => setError(e.message));
	}, []);

	// Persisting is best-effort: a browser with storage blocked must still simulate.
	useEffect(() => {
		if (!data) return;
		try { localStorage.setItem(STORE, JSON.stringify(rows)); } catch { /* not essential */ }
	}, [rows, data]);

	const set = useCallback((id, patch) =>
		setRows(r => ({...r, [id]: {...r[id], ...patch}})), []);

	const reset = useCallback(() => {
		if (!data) return;
		try { localStorage.removeItem(STORE); } catch { /* not essential */ }
		setRows(Object.fromEntries(data.articles.map(a => [a._id, seedRow(a)])));
	}, [data]);

	const model = useMemo(() => {
		if (!data) return null;
		// Rounded PER ROW, then summed — so the footer is the sum of the figures actually
		// printed above it and a reader can verify the column by hand. Summing the raw
		// products and rounding once at the end left the totals two kronor off the booked
		// run-rate on a freshly seeded page.
		const per = a => {
			const r = rows[a._id] ?? seedRow(a);
			return r.on ? Math.round(num(r.price) * num(r.units) * num(r.customers)) : 0;
		};
		const byStream = data.streams.map(s => {
			const arts = data.articles.filter(a => a.stream === s._id);
			return {
				...s, articles: arts,
				scenario: arts.reduce((n, a) => n + per(a), 0),
				current: arts.reduce((n, a) => n + a.actual.run_rate, 0),
			};
		});
		return {
			byStream, per,
			scenario: byStream.reduce((n, s) => n + s.scenario, 0),
			current: byStream.reduce((n, s) => n + s.current, 0),
		};
	}, [data, rows]);

	if (error) return <div className="state">Kunde inte hämta underlaget: {error}</div>;
	if (!model) return <div className="state">Hämtar…</div>;

	const delta = model.scenario - model.current;
	const {from, to, excluded} = data.window;

	return (
		<>
			<p className="sub">
				Scenario över artikelregistret: pris × antal per kund × antal kunder. Ingenting
				här är bokfört och ingenting skrivs till Fortnox. Utgångsläget är fakturerat
				snitt {from} – {to} och är satt så att scenariot startar på exakt nuläget —
				varje skillnad du ser nedan är alltså din egen ändring. {excluded} är utelämnad
				eftersom den bara är delvis bokförd.
			</p>

			<div className="cards">
				<div className="card">
					<span className="card__label"><span className="card__src">scenario</span>Intäkt</span>
					<div className="card__value">{kr(model.scenario)} kr/mån</div>
					<div className="card__hint">{kr(model.scenario * 12)} kr/år</div>
				</div>
				<div className="card">
					<span className="card__label"><span className="card__src">faktura</span>Nuläge</span>
					<div className="card__value">{kr(model.current)} kr/mån</div>
					<div className="card__hint">snitt {from} – {to}</div>
				</div>
				<div className="card">
					<span className="card__label"><span className="card__src">scenario</span>Skillnad</span>
					<div className="card__value">{delta > 0 ? '+' : ''}{kr(delta)} kr/mån</div>
					<div className="card__hint">
						{!model.current ? 'inget nuläge att jämföra med'
							: delta === 0 ? 'oförändrat mot nuläget'
								: `${delta > 0 ? '+' : ''}${Math.round((delta / model.current) * 100)} % mot nuläget`}
					</div>
				</div>
				<div className="card">
					<span className="card__label"><span className="card__src">faktura</span>Aktiva kunder</span>
					<div className="card__value">{data.active_customers}</div>
					<div className="card__hint">taket för «alla kunder»</div>
				</div>
			</div>

			<div className="toolbar">
				<button className="btn" onClick={reset}>Återställ till utfall</button>
				<span className="spacer" />
				<span className="stamp">
					Scenariot sparas i din webbläsare. Det påverkar varken huvudboken eller Fortnox.
				</span>
			</div>

			{model.byStream.map(s => (
				<div className="sim__stream" key={s._id}>
					<div className="sim__head">
						<span className="sim__name">{s.name}</span>
						<span className="stamp">{s.series}xx · konto {s.account}</span>
						<span className="spacer" />
						<button className="btn btn--sm" title={`Sätt antal kunder till ${data.active_customers} på varje rad i strömmen`}
							onClick={() => setRows(r => {
								const next = {...r};
								for (const a of s.articles) next[a._id] = {...next[a._id], customers: data.active_customers, on: true};
								return next;
							})}>Alla {data.active_customers} kunder</button>
						<span className="sim__sum">
							{kr(s.scenario)} kr/mån
							<i className="sim__was">nu {kr(s.current)}</i>
						</span>
					</div>
					<div className="table-wrap">
						<table style={{minWidth: 900}}>
							<thead><tr>
								<th>Artikel</th><th>Enhet</th><th>Listpris</th><th>Fakturerat pris</th>
								<th>Pris i scenario</th><th>Antal/kund</th><th>Kunder</th>
								<th>Kr/mån</th><th>Nuläge</th>
							</tr></thead>
							<tbody>
								{s.articles.map(a => {
									const r = rows[a._id] ?? seedRow(a);
									const value = model.per(a);
									return (
										<tr key={a._id} className={r.on ? undefined : 'sim__off'}>
											<td>
												<label className="sim__pick">
													<input type="checkbox" checked={r.on}
														onChange={e => set(a._id, {on: e.target.checked})} />
													<b>{a._id}</b> {a.name}
												</label>
											</td>
											<td>{a.per ? PER[a.per] ?? a.per : '—'}</td>
											<td>{a.price_sek == null ? '—' : kr(a.price_sek)}</td>
											<td>{a.actual.price == null ? '—' : kr(a.actual.price)}</td>
											<td><Field value={r.price} onChange={v => set(a._id, {price: v})} /></td>
											<td><Field value={r.units} onChange={v => set(a._id, {units: v})} step="0.01" width={68} /></td>
											<td><Field value={r.customers} onChange={v => set(a._id, {customers: v})} step="0.01" width={68} /></td>
											<td className="num--strong">{kr(value)}</td>
											<td>
												{a.actual.run_rate ? kr(a.actual.run_rate) : '—'}
												{/* Billed in fewer months than the window: its run-rate and its
												    billed-month rate are different numbers, and the reader needs
												    to know which one the seed came from. */}
												{a.actual.months_billed > 0 && a.actual.months_billed < data.window.months
													&& <span className="pill">{a.actual.months_billed} av {data.window.months} mån</span>}
											</td>
										</tr>
									);
								})}
							</tbody>
							<tfoot><tr>
								<td>Summa {s.name}</td><td colSpan={6} />
								<td className="num--strong">{kr(s.scenario)}</td>
								<td>{kr(s.current)}</td>
							</tr></tfoot>
						</table>
					</div>
				</div>
			))}

			<p className="foot-note">
				Ett scenario är inte en prognos och blir aldrig ett verifikat. Artiklar utan
				fakturering i fönstret börjar avbockade och med noll kunder — de finns i
				registret men har ingen historik att utgå från, så varje siffra på en sådan rad
				är din egen. Listpris och fakturerat pris hålls isär: Licens ADMIN listas på
				3 000 kr och faktureras på 2 162. Scenariot utgår från det fakturerade — vilken
				av dem som ska gälla framåt är en affärsfråga, inte en avrundning.
			</p>
		</>
	);
}
